import { useContext, useState, useMemo, useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import styles from './Home.module.css';
import { AuthenticationView } from './Authentication';
import { TopStories } from './stories/TopStories';
import { DailyNews } from './daily-news/DailyNews';
import { Magazines } from './magazines/Magazines';
import { MyLibrary } from './library/MyLibrary';
import { Contest } from './contest/Contest';
import { useIsSupportMagazine } from './magazines/Magazines.hooks';
import { HomeContext, ModeType } from './Home.context';
import {
	AuthenticationDialog,
	ModeType as AuthenticationType,
} from './auth/AuthenticationDialog';
import { InstallBtn, Installation } from '../installation/Installation';
import { Issue, NAVBAR_ICON_URL, FAVORITE_KEY } from '../../constants';
import config from '../../config';
import { Icon } from '../../components/icon/Icon';
import { AppContext } from '../../App.context';
import { Modal } from '../../components/modal/Modal';
import { TemplatePoets } from '@/config.type';

import { AboutButton } from '@components/about/About';
import { EyeIndicator } from '../drafts/Drafts';
import { TrackingContext } from '@tracking/Tracking.context';
import {
	ACCESS_TOKEN_EXPIRE_AT_KEY,
	ACCESS_TOKEN_KEY,
	AUTH_EXPIRATION_DATE_KEY,
	IS_AUTHENTICATED_KEY,
} from '@/auth/constants';
// import { isUserAuthenticated } from './auth/AuthenticationDialog';

export const HomeView = () => {
	const query = useQuery();
	const section = query.get('section');
	const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null);
	const [mode, setMode] = useState<ModeType>(getModeValue(section));
	const [isAuthenticated, setIsAuthenticated] = useState(
		config.app.auth.enabled
			? !!localStorage.getItem(IS_AUTHENTICATED_KEY)
			: true,
	);

	const [showInstallation, setShowInstallation] = useState(false);
	const [showLogout, setShowLogout] = useState(false);
	// const [showInstallation, setShowInstallation] = useState(false);

	useEffect(() => {
		if (!localStorage.getItem(FAVORITE_KEY)) {
			localStorage.setItem(FAVORITE_KEY, '[]');
		}
	}, []);

	// Controls Authentication
	const [isAuthOpen, setAuthIsOpen] = useState(true);
	const [authMode, setAuthMode] = useState<AuthenticationType>('login');
	const onSuccess = () => {
		localStorage.setItem(IS_AUTHENTICATED_KEY, 'true');
		setIsAuthenticated(true);
		setAuthIsOpen(false);
	};
	const onClickLogOut = () => {
		if (localStorage.getItem(IS_AUTHENTICATED_KEY)) {
			localStorage.removeItem(IS_AUTHENTICATED_KEY);
		}

		if (localStorage.getItem(ACCESS_TOKEN_KEY)) {
			localStorage.removeItem(ACCESS_TOKEN_KEY);
		}

		if (localStorage.getItem(ACCESS_TOKEN_EXPIRE_AT_KEY)) {
			localStorage.removeItem(ACCESS_TOKEN_EXPIRE_AT_KEY);
		}
		if (config.app.auth.type === 'token') {
			if (localStorage.getItem(AUTH_EXPIRATION_DATE_KEY)) {
				localStorage.removeItem(AUTH_EXPIRATION_DATE_KEY);
			}
		}

		if (localStorage.getItem(FAVORITE_KEY)) {
			localStorage.setItem(FAVORITE_KEY, '[]');
		}

		setIsAuthenticated(false);
		window.location.href = '/';
	};

	if (
		!isAuthenticated &&
		config.app.auth.enabled &&
		(config.app.auth.type === 'login' || config.app.auth.type === 'jwt')
	) {
		return (
			<>
				<Installation
					show={false}
					onCloseClick={() => {
						setShowInstallation(false);
					}}
				/>
				<AuthenticationView onSuccess={onSuccess} />;
			</>
		);
	}

	const onCloseDialog = () => {
		setAuthIsOpen(false);
	};
	const onClickSignIn = () => {
		setAuthIsOpen(true);
		setAuthMode('login');
	};
	const onClickSignUp = () => {
		setAuthIsOpen(true);
		setAuthMode('signup');
	};

	return (
		<HomeContext.Provider
			value={{
				mode,
				isAuthenticated,
				setMode,
				selectedIssue,
				setSelectedIssue,
				setAuthIsOpen,
				setShowInstallation,
				onClickLogOut,
				setShowLogout,
			}}
		>
			<InstallationDialog
				setShowInstallation={setShowInstallation}
				show={showInstallation}
			/>
			<LogOutDialog
				show={showLogout}
				setShowLogout={setShowLogout}
				onClickLogOut={onClickLogOut}
			/>
			<div className={styles['home']}>
				<div className={styles['view']}>
					<Topbar />
					<Content />
					<Navbar />
				</div>
				<AuthenticationDialog
					mode={authMode}
					setMode={setAuthMode}
					onClickSignUp={onClickSignUp}
					onClickLogIn={onClickSignIn}
					onCloseDialog={onCloseDialog}
					isOpen={isAuthenticated ? false : isAuthOpen}
					onSuccess={onSuccess}
				/>
			</div>
		</HomeContext.Provider>
	);
};

function getModeValue(section: string | null): ModeType {
	if (
		section === 'library' ||
		section === 'daily-news' ||
		section === 'top-stories'
	) {
		return section;
	}

	return 'magazines';
}

function useQuery() {
	const { search } = useLocation();

	return useMemo(() => new URLSearchParams(search), [search]);
}

const Topbar = () => {
	const { website } = config.build;
	console.log(`WEBSITE: "${website}"`);
	const [searchParams, setSearchParams] = useSearchParams();
	const { isInstalled } = useContext(AppContext);
	const { selectedIssue, setSelectedIssue, setShowInstallation } = useContext(
		HomeContext,
	);
	if (selectedIssue) {
		return (
			<div className={styles['topbar']}>
				<div>
					<button
						type="button"
						onClick={() => {
							const updatedSearchParams = new URLSearchParams(
								searchParams.toString(),
							);
							updatedSearchParams.set('id', '');
							updatedSearchParams.delete('id');
							setSearchParams(updatedSearchParams.toString());
							setSelectedIssue(null);
						}}
					>
						<Icon name={'arrow-left'} color={'#000'} />
					</button>
				</div>
				<div className={styles['title']}>
					{website ? (
						<a href={website} target="_blank" rel="noreferrer">
							{' '}
							<img src={NAVBAR_ICON_URL} alt="Logo" />{' '}
						</a>
					) : (
						<img src={NAVBAR_ICON_URL} alt="Logo" />
					)}
				</div>
				<div></div>
			</div>
		);
	}

	return (
		<div className={styles['topbar']}>
			<div>
				<AboutButton />
				<EyeIndicator />
			</div>
			<div className={styles['title']}>
				{website ? (
					<a href={website} target="_blank" rel="noreferrer">
						{' '}
						<img src={NAVBAR_ICON_URL} alt="Logo" />{' '}
					</a>
				) : (
					<img src={NAVBAR_ICON_URL} alt="Logo" />
				)}
			</div>
			<div>
				<ButtonBar />
				{!isInstalled ? (
					<InstallBtn
						onClick={() => {
							console.log(`I click the button`);
							setShowInstallation(true);
						}}
					/>
				) : null}
			</div>
		</div>
	);
};

function Content() {
	const { mode } = useContext(HomeContext);
	let component = null;
	switch (mode) {
		case 'magazines':
			component = <Magazines />;
			break;
		case 'daily-news':
			component = <DailyNews />;
			break;
		case 'top-stories':
			component = <TopStories />;
			break;
		case 'contest':
			component = <Contest />;
			break;
		default:
			component = <MyLibrary />;
			break;
	}
	return <div className={styles['content']}>{component}</div>;
}

function Navbar() {
	return (
		<nav>
			<ButtonBar />
		</nav>
	);
}

function ButtonBar() {
	const ref = useRef<HTMLDivElement>(null);
	const { mode, setMode, isAuthenticated } = useContext(HomeContext);
	const template = config.app.template as TemplatePoets;
	useEffect(() => {
		const root = document.documentElement;
		if (!root) {
			return;
		}
		if (!ref) {
			return;
		}
		if (!ref.current) {
			return;
		}

		const current = ref.current;
		if (current.children.length) {
			root.style.setProperty(
				'--bottom-bar-height',
				'calc(var(--navbar-height) + var(--safe-area-inset))',
			);
		} else {
			root.style.setProperty('--bottom-bar-height', '0px');
		}
	}, [ref]);

	return (
		<div ref={ref} className={styles['button-bar']}>
			{template.config.feed && (
				<>
					<button
						type={'button'}
						title="Latest"
						key={'latest'}
						data-text="Latest"
						onClick={() => {
							setMode('top-stories');
						}}
						className={[
							mode === 'top-stories' ? styles['active'] : '',
							'tooltip',
							'down',
						].join(' ')}
					>
						<Icon color="#000" name="align-left" />
						Latest
					</button>
					<button
						type={'button'}
						key={'daily-news'}
						title="Daily News"
						data-text="Daily News"
						onClick={() => {
							setMode('daily-news');
						}}
						className={[
							mode === 'daily-news' ? styles['active'] : '',
							'tooltip',
							'down',
						].join(' ')}
					>
						<Icon color="#000" name="globe" />
						Daily News
					</button>
				</>
			)}
			{(template.config.feed ||
				(config.app.auth.enabled && isAuthenticated) ||
				config.app.favorite) && (
				<button
					type={'button'}
					key={'magazines'}
					title="Magazines"
					data-text="Magazines"
					onClick={() => {
						setMode('magazines');
					}}
					className={[
						mode === 'magazines' ? styles['active'] : '',
						'tooltip',
						'down',
					].join(' ')}
				>
					<Icon color="#000" name="library" />
					Magazine
				</button>
			)}
			{config.app.favorite && <BookmarkIcon />}
			{isAuthenticated && <ButtonLogOut />}
		</div>
	);
}

const BookmarkIcon = () => {
	const { mode, setMode } = useContext(HomeContext);
	const isDigitalAccess = useIsSupportMagazine();
	if (!isDigitalAccess) {
		return null;
	}
	return (
		<button
			type={'button'}
			title="Favorites"
			key={'favorites'}
			data-text="Favorites"
			onClick={() => {
				setMode('library');
			}}
			className={[
				mode === 'library' ? styles['active'] : '',
				'tooltip',
				'down',
			].join(' ')}
		>
			<Icon color="#000" name="bookmark" />
			Favorites
		</button>
	);
};

export const ButtonLogOut = () => {
	const { setShowLogout, isAuthenticated } = useContext(HomeContext);
	if (!config.app.auth.enabled) {
		return null;
	}
	if (!isAuthenticated) {
		return null;
	}
	return (
		<button
			type={'button'}
			title="Log out"
			key={'log-out'}
			data-text="Log out"
			onClick={() => {
				setShowLogout(true);
			}}
			className={'tooltip down'}
		>
			<Icon color="#000" name="logout" />
			Log Out
		</button>
	);
};

export const LogOutDialog = ({
	show,
	setShowLogout,
	onClickLogOut,
}: LogOutDialog) => {
	const { sendEvent } = useContext(TrackingContext);
	return (
		<Modal
			classNames={[styles['logout-dialog-container']]}
			hasCloseBtn={true}
			isOpen={show}
			onClose={() => {
				setShowLogout(false);
			}}
		>
			<div className={styles['logout-dialog']}>
				<img src={NAVBAR_ICON_URL} alt="logo" />
				<h3>Are you sure you want to log out?</h3>
				<button
					type="button"
					onClick={() => {
						sendEvent({
							type: 'BTN_CLICK_LOGOUT',
						});
						setShowLogout(false);
						onClickLogOut();
					}}
				>
					Log Out
				</button>
			</div>
		</Modal>
	);
};

interface LogOutDialog {
	show: boolean;
	setShowLogout: (l: boolean) => void;
	onClickLogOut: () => void;
}

export const InstallationDialog = ({
	show,
	setShowInstallation,
}: InstallationDialogProps) => {
	const { device } = useContext(AppContext);
	const message = device?.isTouchable ? `Add to Home Screen` : `Add to Dock`;
	return (
		<Modal
			classNames={[styles['installation-dialog-container']]}
			hasCloseBtn={true}
			isOpen={show}
			onClose={() => {
				setShowInstallation(false);
			}}
		>
			<div className={styles['installation-dialog']}>
				<img src={NAVBAR_ICON_URL} alt="logo" />
				<h3>Install App</h3>
				<p>
					Install this app for quick and easy access <br /> when
					you&apos;re on the go.
				</p>
				{isSafari() ? (
					<ol>
						<li>
							Tap the <Share /> button on your screen.
						</li>
						<li>
							Select <b>&lsquo;{message}&rsquo;</b>.
						</li>
						<li>
							Tap <b>&lsquo;Add&rsquo;</b>.
						</li>
					</ol>
				) : (
					<ol>
						<li>
							Tap the Menu icon (three vertical dots)
							<br /> in the top-right corner of your screen.
						</li>
						<li>
							Select <b>&lsquo;Add to Home screen&rsquo;</b>.
						</li>
						<li>
							Press <b>&lsquo;Install&rsquo;</b>.
						</li>
					</ol>
				)}
			</div>
		</Modal>
	);
};

export interface InstallationDialogProps {
	show: boolean;
	setShowInstallation: (l: boolean) => void;
}

export function Share() {
	return (
		<svg
			width="24"
			height="24"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
			<polyline points="16 6 12 2 8 6"></polyline>
			<line x1="12" y1="2" x2="12" y2="15"></line>
		</svg>
	);
}

export function isSafari() {
	return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}
